import React from 'react';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const foldChar = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');

const foldString = (value: string) => foldChar(value).toLowerCase();

export interface TextHighlightSpan {
  start: number;
  end: number;
  style?: 'underline' | 'highlight' | 'bold';
}

const foldStringWithMap = (value: string) => {
  const foldedChars: string[] = [];
  const indexMap: number[] = [];

  for (let i = 0; i < value.length; i += 1) {
    const folded = foldChar(value[i]);
    if (!folded) continue;
    for (let j = 0; j < folded.length; j += 1) {
      foldedChars.push(folded[j]);
      indexMap.push(i);
    }
  }

  return { folded: foldedChars.join('').toLowerCase(), indexMap };
};

export const buildHighlightedNodes = (text: string, query?: string) => {
  if (!query || !text) return text;
  const terms = query.trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return text;

  const { folded, indexMap } = foldStringWithMap(text);
  const foldedTerms = terms.map((term) => foldString(term)).filter(Boolean);

  const ranges: Array<{ start: number; end: number }> = [];
  foldedTerms.forEach((term) => {
    if (!term) return;
    let searchIndex = 0;
    while (searchIndex < folded.length) {
      const matchIndex = folded.indexOf(term, searchIndex);
      if (matchIndex === -1) break;
      ranges.push({ start: matchIndex, end: matchIndex + term.length });
      searchIndex = matchIndex + term.length;
    }
  });

  if (!ranges.length) {
    const pattern = terms.map(escapeRegExp).filter(Boolean).join('|');
    if (!pattern) return text;
    const regex = new RegExp(`(${pattern})`, 'ig');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      index % 2 === 1 ? (
        <mark key={`${part}-${index}`} style={{ backgroundColor: '#F6E05E', padding: '0 2px' }}>
          {part}
        </mark>
      ) : (
        <span key={`${part}-${index}`}>{part}</span>
      ),
    );
  }

  ranges.sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [];
  ranges.forEach((range) => {
    const last = merged[merged.length - 1];
    if (!last || range.start > last.end) {
      merged.push({ ...range });
    } else if (range.end > last.end) {
      last.end = range.end;
    }
  });

  const nodes: React.ReactNode[] = [];
  let currentIndex = 0;

  merged.forEach((range, idx) => {
    const originalStart = indexMap[range.start];
    const originalEnd = indexMap[range.end - 1];
    if (originalStart == null || originalEnd == null) return;

    const safeStart = Math.max(0, originalStart);
    const safeEnd = Math.min(text.length, originalEnd + 1);

    if (safeStart > currentIndex) {
      nodes.push(
        <span key={`text-${idx}-${currentIndex}`}>{text.slice(currentIndex, safeStart)}</span>,
      );
    }

    nodes.push(
      <mark
        key={`mark-${idx}-${safeStart}`}
        style={{ backgroundColor: '#F6E05E', padding: '0 2px' }}
      >
        {text.slice(safeStart, safeEnd)}
      </mark>,
    );

    currentIndex = safeEnd;
  });

  if (currentIndex < text.length) {
    nodes.push(<span key={`text-tail-${currentIndex}`}>{text.slice(currentIndex)}</span>);
  }

  return nodes;
};

const clampSpans = (text: string, spans?: TextHighlightSpan[]) => {
  if (!spans || !spans.length) return [] as TextHighlightSpan[];

  const textLength = text.length;
  return spans
    .map((span) => ({
      start: Math.max(0, Math.min(textLength, Math.floor(span.start))),
      end: Math.max(0, Math.min(textLength, Math.floor(span.end))),
      style: span.style,
    }))
    .filter((span) => span.end > span.start)
    .sort((left, right) => left.start - right.start || left.end - right.end);
};

const getHighlightStyle = (style?: TextHighlightSpan['style']) => {
  switch (style) {
    case 'bold':
      return {
        backgroundColor: '#FDE68A',
        fontWeight: 800,
        borderRadius: '4px',
        padding: '0 2px',
      };
    case 'highlight':
      return {
        backgroundColor: '#FDE68A',
        borderRadius: '4px',
        padding: '0 2px',
      };
    case 'underline':
    default:
      return {
        backgroundColor: '#FEF3C7',
        textDecoration: 'underline',
        textDecorationThickness: '2px',
        textDecorationColor: '#D97706',
        textUnderlineOffset: '2px',
        fontWeight: 700,
        borderRadius: '4px',
        padding: '0 2px',
      };
  }
};

export const buildHighlightedSpanNodes = (text: string, spans?: TextHighlightSpan[]) => {
  if (!text) return text;

  const normalizedSpans = clampSpans(text, spans);
  if (!normalizedSpans.length) {
    return text;
  }

  const nodes: React.ReactNode[] = [];
  let cursor = 0;

  normalizedSpans.forEach((span, index) => {
    if (span.start > cursor) {
      nodes.push(<span key={`plain-${index}-${cursor}`}>{text.slice(cursor, span.start)}</span>);
    }

    nodes.push(
      <span key={`highlight-${index}-${span.start}-${span.end}`} style={getHighlightStyle(span.style)}>
        {text.slice(span.start, span.end)}
      </span>,
    );

    cursor = Math.max(cursor, span.end);
  });

  if (cursor < text.length) {
    nodes.push(<span key={`plain-tail-${cursor}`}>{text.slice(cursor)}</span>);
  }

  return nodes;
};
